import React from 'react';
import { partialCoverageLabel, type AssetTierInfo, type PolicyExclusion } from '../utils/assetUniverse';

// Universe tier badge (owner rulings OR-UNI-20261004-01..05): "Qualified" or "Partial coverage · NN%" wherever an
// asset is chosen or headed. Same chip style as the Analysis header chips (mono, 10px, 2px radius).
const BASE = 'font-mono text-[10px] tracking-wider px-2 py-0.5 rounded-[2px] border whitespace-nowrap';

export default function UniverseTierBadge({ info, run, className = '' }: { info: AssetTierInfo | null; run?: string; className?: string }) {
  if (!info) return null;
  if (info.tier === 'QUALIFIED') {
    return (
      <span data-testid="tier-badge" data-tier="QUALIFIED" className={`${BASE} border-[color:rgb(var(--c-gold-rgb)/0.45)] text-[color:var(--c-gold)] ${className}`}
        title={`SAA Qualified: passed the SAA Product Eligibility Gate${run ? ` · ${run}` : ''}`}>Qualified</span>
    );
  }
  return (
    <span data-testid="tier-badge" data-tier="ANALYSABLE" className={`${BASE} border-[color:rgb(var(--c-amber-rgb)/0.45)] text-[color:var(--c-amber)] ${className}`}
      title={`Analysable · partial coverage: every computed metric is shown; the composite score is withheld while a framework axis has no admissible evidence${typeof info.coverage === 'number' ? ' (percentage = canonical scoreable coverage)' : ''}${run ? ` · ${run}` : ''}`}>
      {partialCoverageLabel(info.coverage)}
    </span>
  );
}

// Held asset excluded by an explicit universe policy (owner 2026-10-07, e.g. WBTC): a small neutral note — the position
// stays as entered, nothing is substituted. Rendered only for a non-member (a member still shows its tier badge).
export function PolicyExclusionNote({ exclusion, className = '' }: { exclusion: PolicyExclusion | null; className?: string }) {
  if (!exclusion) return null;
  const label = exclusion.reason_code === 'EXCLUDED_BY_POLICY_WRAPPED_REPRESENTATION'
    ? 'Excluded by universe policy · wrapped representation' : 'Excluded by universe policy';
  return (
    <span data-testid="policy-exclusion-note" data-reason-code={exclusion.reason_code} className={`${BASE} border-cmd-border text-cmd-textMute ${className}`}
      title={`${exclusion.reason}${exclusion.decision_ref ? ` · ${exclusion.decision_ref}` : ''}`}>{label}</span>
  );
}
